import type { Asset2CountryReview } from '../../apis/graphql';
import { Asset2ReviewStatus } from '../../apis/graphql';
import { CustomRejectionReason } from '../../common/constants';
import type { Nullable } from '../../models';

export type DerivedAssetReview = {
  review: Asset2ReviewStatus;
  rejectionReasons: string[];
  approvedPublishers: string[];
};

export const deriveAssetReviewForCountryList = (
  countryReviews: Asset2CountryReview[],
  countryList: string[],
  publisherList?: Nullable<string[]>
): DerivedAssetReview => {
  const countrySet = new Set(countryList);
  const matchingReviews = countryReviews.filter(
    (countryReview) => countryReview.country && countrySet.has(countryReview.country)
  );

  if (!matchingReviews.length) {
    return {
      review: Asset2ReviewStatus.Declined,
      rejectionReasons: [CustomRejectionReason.countryMismatch],
      approvedPublishers: [],
    };
  }

  const acceptedMatchingReviews = matchingReviews.filter(
    (countryReview) => countryReview.status === Asset2ReviewStatus.Accepted
  );

  const approvedPublishers = [
    ...new Set(acceptedMatchingReviews.flatMap((countryReview) => countryReview.allowedPublishers ?? [])),
  ];

  const publisherMatches = approvedPublishers.some((publisher) => publisherList?.includes(publisher));

  if (publisherMatches) {
    return {
      review: Asset2ReviewStatus.Accepted,
      rejectionReasons: [],
      approvedPublishers,
    };
  }

  if (matchingReviews.some((countryReview) => countryReview.status === Asset2ReviewStatus.Pending)) {
    return {
      review: Asset2ReviewStatus.Pending,
      rejectionReasons: [],
      approvedPublishers,
    };
  }

  if (matchingReviews.some((countryReview) => countryReview.status === Asset2ReviewStatus.SpDeferred)) {
    return {
      review: Asset2ReviewStatus.SpDeferred,
      rejectionReasons: [],
      approvedPublishers,
    };
  }

  if (acceptedMatchingReviews.length > 0) {
    return {
      review: Asset2ReviewStatus.Declined,
      rejectionReasons: [CustomRejectionReason.publisherMismatch],
      approvedPublishers,
    };
  }

  const rejectionReasons = matchingReviews.reduce<string[]>((reasons, countryReview) => {
    if (countryReview.status !== Asset2ReviewStatus.Declined) {
      return reasons;
    }

    (countryReview.declines ?? []).forEach((decline) => {
      if (decline.reason) {
        reasons.push(decline.reason);
      }
    });

    return reasons;
  }, []);

  return {
    review: Asset2ReviewStatus.Declined,
    rejectionReasons,
    approvedPublishers,
  };
};
